
/* eslint-disable  @typescript-eslint/no-explicit-any */

import Enumerable from "linq";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { AccessType, ColumnType, ConfigurationType, DateRangeType, DurationTimeframeTypeEnum, Scope, SortOrder, Style, TimeframeType } from "../../common/enums/DRCXEnums";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { NumericalFunctionEnum } from "../../common/enums/NumericalFunctionEnum";
import { TrendConfigurationTypeEnum } from "../../common/enums/TrendConfigurationTypeEnum";
import { AccessPermission } from "../../common/models/AccessPermission";
import { IDRCXConfigurationDTOBase } from "../../common/models/drcx/DRCXConfigurationDTOBase";
import { DRCXConfigurationModelBase } from "../../common/models/drcx/DRCXConfigurationModelBase";
import { DRCXConfigurationSectionColumnModel } from "../../common/models/drcx/DRCXConfigurationSectionColumnModel";
import { IDRCXConfigurationSectionDTOBase } from "../../common/models/drcx/DRCXConfigurationSectionDTOBase";
import { DRCXConfigurationSectionModelBase } from "../../common/models/drcx/DRCXConfigurationSectionModelBase";
import { IDRCXConfigurationSectionObjectDTO } from "../../common/models/drcx/DRCXConfigurationSectionObjectDTO";
import { DRCXConfigurationSectionObjectModel } from "../../common/models/drcx/DRCXConfigurationSectionObjectModel";
import { IDRCXConfigurationTimeframeDTO } from "../../common/models/drcx/DRCXConfigurationTimeframeDTO";
import { DRCXTimeAndDuration, IDRCXTimeAndDuration } from "../../common/models/drcx/DRCXTimeAndDuration";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { parseObjectEquatableKey } from "../../common/utilities/drcx/ParseObjectEquatableKey";
import UUID from "../../common/utilities/Uuid";
import i18n from "../localization-service/i18n";
import { IUIChartPropertiesClient, UIChartPropertiesClient } from "../trending-data-service/UIChartProperties";

export interface ITrendConfigurationSectionDTO extends IDRCXConfigurationSectionDTOBase {

    acrossDevices: boolean;
    includeOilTanks: boolean;
    includeWaterTanks: boolean;
    plotSeriesOnSameYAxis: boolean;
    sectionUniqueID: string;
}

export interface ITrendConfigurationDTO extends IDRCXConfigurationDTOBase {

    autoRefresh: boolean;
    combineAcrossDeviceAxes: boolean;
    comparisonTimeframe?: IDRCXConfigurationTimeframeDTO;
    displayAlarms: boolean;
    displayDataQualityEvents: boolean;
    displayDiagnosticEvents: boolean;
    displayManualEvents: boolean;
    displayLegend: boolean;
    displayMemos: boolean;
    displaySectionsStacked: boolean;
    displayTabularData: boolean;
    displaySettingsChanges: boolean;
    displaySimulationDataQualityEvents: boolean;
    displaySimulationDiagnosticEvents: boolean;
    hasComparisonTimeframe: boolean;
    trendConfigurationType: TrendConfigurationTypeEnum;

    // This is only the overide chart property settings. These are optional.
    // This should be undefined unless the user changed chart settings. 
    uiChartProperties?: IUIChartPropertiesClient;

    toDTO?(): ITrendConfigurationDTO;
}

export class TrendConfigurationSectionModel extends DRCXConfigurationSectionModelBase /*implements IDRCXConfigurationSection*/ {

    public acrossDevices: boolean = false;
    public dataType: eLynxObjectTypeEnum | undefined = undefined;
    public includeOilTanks: boolean = false;
    public includeWaterTanks: boolean = false;
    public plotSeriesOnSameYAxis: boolean = false;
    public sectionUniqueID: string = UUID.generate();

    // UI Only
    public trendConfigurationType: TrendConfigurationTypeEnum = TrendConfigurationTypeEnum.Device;

    public constructor(dto?: ITrendConfigurationSectionDTO) {
        super(dto);

        if (dto) {

            this.acrossDevices = dto.acrossDevices;
            this.includeOilTanks = dto.includeOilTanks;
            this.includeWaterTanks = dto.includeWaterTanks;
            this.plotSeriesOnSameYAxis = dto.plotSeriesOnSameYAxis;
            this.sectionUniqueID = dto.sectionUniqueID;
        }
    }

    public clone(): TrendConfigurationSectionModel {
        const model = new TrendConfigurationSectionModel(this.toDTO());
        // model.isExpanded = this.isExpanded;
        // model.isValid = this.isValid;
        // UI Only
        model.trendConfigurationType = this.trendConfigurationType;
        return model;
    }

    public static createDefault(sectionOrder: number, eLynxObject: IeLynxObject | undefined): TrendConfigurationSectionModel {

        const newSection: TrendConfigurationSectionModel = new TrendConfigurationSectionModel({
            name: `New Section ${sectionOrder}`,
            order: sectionOrder,
            // Trending: always uses Linear for the Style.
            style: Style.Linear,
            dateSortOrder: SortOrder.Ascending,
            deviceSortOrder: SortOrder.Ascending,
            timeframe: {
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: 0,
                    rangeType: DateRangeType.Invalid
                },
                duration: {
                    duration: 0,
                    rangeType: DateRangeType.Invalid
                },
                currentDateOffset: {
                    duration: 0,
                    rangeType: DateRangeType.Invalid
                },
                durationTimeframeType: undefined,
            },
            acrossDevices: false,
            includeOilTanks: false,
            includeWaterTanks: false,
            plotSeriesOnSameYAxis: false,
            // Trending: Seems to always be set to true
            useTatEffectiveDate: true,
            sectionUniqueID: UUID.generate(),
            columns: [
            ],
            objects: eLynxObject?.objectType !== eLynxObjectTypeEnum.Group
                ? eLynxObject?.objectType ? [
                    {
                        applicationType: undefined,
                        deviceType: undefined,
                        excludeObject: false,
                        id: eLynxObject.id,
                        includeDynamicGroups: true,
                        includeSubgroups: false,
                        includeWellDevices: true,
                        liftType: undefined,
                        objectType: eLynxObject.objectType,
                        order: 0,
                        ruleType: undefined,
                        schedule: undefined,
                        tankSubType: undefined,
                        targetObjectType: eLynxObject.objectType
                    }
                ]
                    : []
                : []
        });

        newSection.setTrendConfigurationType(
            TrendConfigurationModel.getConfigurationType(eLynxObject?.objectType ?? eLynxObjectTypeEnum.Device));

        return newSection;
    }

    public /*override*/ addObject(name: string, equatableKey: string, deviceType?: DeviceTypesEnum): void {
        if (!Enumerable.from(this.objects).any((obj: DRCXConfigurationSectionObjectModel) => obj.equatableKey === equatableKey)) {
            const object: DRCXConfigurationSectionObjectModel = new DRCXConfigurationSectionObjectModel({
                name: name,
                order: this.objects.length,
                //deviceType: deviceType,
                ...parseObjectEquatableKey(equatableKey)
            });
            //object.equatableKey = equatableKey;
            if (object.objectType === eLynxObjectTypeEnum.Group) {
                object.targetObjectType = this.dataType;
            } else {
                object.targetObjectType = object.objectType;
            }

            this.objects.push(object);
        }
    }

    public toDTO(): ITrendConfigurationSectionDTO {
        //return this._toDTO(<any>{});
        return this._toDTO({
            acrossDevices: false,
            includeOilTanks: false,
            includeWaterTanks: false,
            plotSeriesOnSameYAxis: false,
            sectionUniqueID: ''
        });
    }

    // public /*override*/ validate(): boolean {

    //     if (this.trendConfigurationType === TrendConfigurationTypeEnum.Group) {
    //         // Must have a valid name, contain at least one TAT column, and contain at least one object (group/device/tank)
    //         this.isValid = typeof this.name == "string" &&
    //             this.columns &&
    //             Enumerable.from(this.columns).any(c => c.columnType === ColumnType.Tat ||
    //                 c.columnType === ColumnType.TatType) &&
    //             this.objects &&
    //             this.objects.length > 0;
    //     } else {
    //         // Must have a valid name and contain at least one TAT column.
    //         this.isValid = typeof this.name == "string" &&
    //             this.columns &&
    //             Enumerable.from(this.columns).any(c => c.columnType === ColumnType.Tat ||
    //                 c.columnType === ColumnType.TatType);
    //     }

    //     return this.isValid;
    // }

    public /*override*/ hasDeprecatedFeatures(): boolean {
        return false;
        // TODO: Need to check if object collection has mix of tanks and devices
        //return this.includeTanks;
    }

    protected _toDTO(dto: ITrendConfigurationSectionDTO): ITrendConfigurationSectionDTO {
        super._toDTO(dto);

        dto.acrossDevices = this.acrossDevices;
        dto.includeOilTanks = this.includeOilTanks;
        dto.includeWaterTanks = this.includeWaterTanks;
        dto.plotSeriesOnSameYAxis = this.plotSeriesOnSameYAxis;
        dto.sectionUniqueID = this.sectionUniqueID;

        return dto;
    }

    // UI Related
    public setTrendConfigurationType(trendConfigurationType: TrendConfigurationTypeEnum) {
        this.trendConfigurationType = trendConfigurationType;
    }
}

export class TrendingTimeAndDuration extends DRCXTimeAndDuration {

    public constructor() {
        super();

        this.durationType = DurationTimeframeTypeEnum.MultipleTimeFrames;

        // durationType == SpecificStartOrStop
        // Default to the 7 days before now plus the multipleStartOffset 6 days before now
        this.fixedStartDate = undefined;
        this.fixedStartDateRangeType = DateRangeType.Raw;
        this.fixedEndDate = new Date();
        this.fixedEndDateRangeType = DateRangeType.Raw;

        // durationType == MultipleTimeFrames
        this.multipleStartOffset = undefined; // -6;
        this.multipleStartRangeType = DateRangeType.Day;
        this.multipleDuration = undefined;
        this.multipleDurationRangeType = DateRangeType.Invalid;

        // durationType == CurrentSingleTimeFrame
        this.currentRangeType = DateRangeType.Month;

        // durationType == PreviousSingleTimeFrame
        this.previousRangeType = DateRangeType.Hour;

        this.runToCurrentDate = false;
        this.currentDateOffset = 0;
        this.currentDateRangeType = undefined;
    }
}

export class TrendConfigurationModel extends DRCXConfigurationModelBase<TrendConfigurationSectionModel> /*implements IDRCXConfiguration*/ {

    public autoRefresh: boolean = false;
    public combineAcrossDeviceAxes: boolean = false;
    public comparisonTimeAndDuration: IDRCXTimeAndDuration = new TrendingTimeAndDuration();
    public comparisonPreviousFixedStartDate: Date | undefined = this.comparisonTimeAndDuration.fixedStartDate ? new Date(this.comparisonTimeAndDuration.fixedStartDate) : undefined;
    public comparisonPreviousFixedEndDate: Date | undefined = this.comparisonTimeAndDuration.fixedEndDate ? new Date(this.comparisonTimeAndDuration.fixedEndDate) : undefined;
    public displayAlarms: boolean = false;
    public displayDataQualityEvents: boolean = false;
    public displayDiagnosticEvents: boolean = false;
    public displayManualEvents: boolean = false;
    public displayLegend: boolean = false;
    public displayMemos: boolean = false;
    public displaySectionsStacked: boolean = false;
    public displayTabularData: boolean = false;
    public displaySettingsChanges: boolean = false;
    public displaySimulationDataQualityEvents: boolean = false;
    public displaySimulationDiagnosticEvents: boolean = false;
    public hasComparisonTimeframe: boolean = false;
    public previousFixedStartDate: Date | undefined = this.fixedStartDate ? new Date(this.fixedStartDate) : undefined;
    public previousFixedEndDate: Date | undefined = this.fixedEndDate ? new Date(this.fixedEndDate) : undefined;
    public trendConfigurationType: TrendConfigurationTypeEnum = TrendConfigurationTypeEnum.Device;

    // This is only the override chart property settings. These are optional.
    // This should be undefined unless the user changed chart settings. 
    uiChartProperties: IUIChartPropertiesClient | undefined = undefined;

    public constructor(dto?: ITrendConfigurationDTO) {
        super(dto);

        this.configurationType = ConfigurationType.Trending;

        if (dto) {
            this.autoRefresh = dto.autoRefresh;
            this.combineAcrossDeviceAxes = dto.combineAcrossDeviceAxes;
            this.comparisonTimeAndDuration = TrendConfigurationModel.getComparisonTimeAndDuration(dto.comparisonTimeframe);
            this.displayAlarms = dto.displayAlarms;
            this.displayDataQualityEvents = dto.displayDataQualityEvents;
            this.displayDiagnosticEvents = dto.displayDiagnosticEvents;
            this.displayManualEvents = dto.displayManualEvents;
            this.displayLegend = dto.displayLegend;
            this.displayMemos = dto.displayMemos;
            this.displaySectionsStacked = dto.displaySectionsStacked;
            this.displayTabularData = dto.displayTabularData;
            this.displaySettingsChanges = dto.displaySettingsChanges;
            this.displaySimulationDataQualityEvents = dto.displaySimulationDataQualityEvents;
            this.displaySimulationDiagnosticEvents = dto.displaySimulationDiagnosticEvents;
            this.hasComparisonTimeframe = dto.hasComparisonTimeframe;
            this.trendConfigurationType = dto.trendConfigurationType;
            this.uiChartProperties = dto.uiChartProperties !== undefined ? new UIChartPropertiesClient(dto.uiChartProperties) : undefined;
        }

        // Trending always overrides the instance dates.
        this.overrideInstanceDates = true;
    }

    public clone(): TrendConfigurationModel {
        const model = new TrendConfigurationModel(this.toDTO());

        model.comparisonPreviousFixedStartDate = this.comparisonPreviousFixedStartDate ? new Date(this.comparisonPreviousFixedStartDate) : undefined;
        model.comparisonPreviousFixedEndDate = this.comparisonPreviousFixedEndDate ? new Date(this.comparisonPreviousFixedEndDate) : undefined;

        model.previousFixedStartDate = this.previousFixedStartDate ? new Date(this.previousFixedStartDate) : undefined;
        model.previousFixedEndDate = this.previousFixedEndDate ? new Date(this.previousFixedEndDate) : undefined;

        return model;
    }

    public static createDefault(configurationId: number, eLynxObject: IeLynxObject): TrendConfigurationModel {

        const configuration: TrendConfigurationModel = new TrendConfigurationModel({
            id: configurationId,
            name: i18n.t("trendingConfig.newConfigName") as string,
            configurationType: ConfigurationType.Trending,
            comparisonTimeframe: {
                durationTimeframeType: DurationTimeframeTypeEnum.MultipleTimeFrames,
                calculatedStartDateOffset: {
                    // Use a negate formatter
                    duration: -6,
                    rangeType: DateRangeType.Day
                }
            },
            hasComparisonTimeframe: false,
            access: AccessType.Private,
            userPermissions: new Array<AccessPermission>(),
            scope: Scope.User,
            created: new Date(),
            forAllGroups: eLynxObject.objectType === eLynxObjectTypeEnum.Group ? false : true,
            overrideInstanceDates: true,
            timeframe: {
                durationTimeframeType: DurationTimeframeTypeEnum.MultipleTimeFrames,
                calculatedStartDateOffset: {
                    // Use a negate formatter
                    duration: -6,
                    rangeType: DateRangeType.Day
                },
                duration: {
                    duration: 7,
                    rangeType: DateRangeType.Day
                }
            },
            trendConfigurationType: TrendConfigurationModel.getConfigurationType(eLynxObject.objectType),
            sections: [
                TrendConfigurationSectionModel.createDefault(0, eLynxObject)
            ],

            autoRefresh: false,
            combineAcrossDeviceAxes: false,
            displayAlarms: false,
            displayDataQualityEvents: false,
            displayDiagnosticEvents: false,
            displayManualEvents: false,
            displayLegend: true,
            displayMemos: false,
            displaySectionsStacked: false,
            displayTabularData: false,
            displaySettingsChanges: false,
            displaySimulationDataQualityEvents: false,
            displaySimulationDiagnosticEvents: false
        });

        // ?????
        configuration.uiChartProperties = new UIChartPropertiesClient();

        return configuration;
    }

    public createSection(section: ITrendConfigurationSectionDTO | TrendConfigurationSectionModel): TrendConfigurationSectionModel {
        return new TrendConfigurationSectionModel(section);
    }

    public static getComparisonTimeAndDuration(comparisonTimeframe: IDRCXConfigurationTimeframeDTO | undefined) {

        const timeAndDuration: IDRCXTimeAndDuration = new TrendingTimeAndDuration();

        timeAndDuration.durationType = comparisonTimeframe?.durationTimeframeType ?
            comparisonTimeframe.durationTimeframeType : DurationTimeframeTypeEnum.MultipleTimeFrames;

        if (comparisonTimeframe !== undefined && comparisonTimeframe) {

            if (typeof comparisonTimeframe.durationTimeframeType === "string") {
                throw Error("Enum serialized to string instead of enum (number in json). Check special WebAPI serialization.");
            }

            if (comparisonTimeframe.durationTimeframeType ===
                DurationTimeframeTypeEnum.MultipleTimeFrames) {
                if (typeof comparisonTimeframe.calculatedStartDateOffset !== "undefined") {
                    timeAndDuration.multipleStartOffset = comparisonTimeframe.calculatedStartDateOffset
                        ? comparisonTimeframe.calculatedStartDateOffset.duration
                        : undefined;
                    timeAndDuration.multipleStartRangeType = comparisonTimeframe.calculatedStartDateOffset
                        ? comparisonTimeframe.calculatedStartDateOffset.rangeType
                        : undefined;
                }
                if (typeof comparisonTimeframe.duration !== "undefined") {
                    timeAndDuration.multipleDuration = comparisonTimeframe.duration
                        ? comparisonTimeframe.duration.duration
                        : undefined;
                    timeAndDuration.multipleDurationRangeType = comparisonTimeframe.duration
                        ? comparisonTimeframe.duration.rangeType
                        : undefined;
                }
            } else if (comparisonTimeframe.durationTimeframeType ===
                DurationTimeframeTypeEnum.CurrentSingleTimeFrame) {
                if (typeof comparisonTimeframe.duration !== "undefined") {
                    timeAndDuration.currentRangeType = comparisonTimeframe.duration
                        ? comparisonTimeframe.duration.rangeType
                        : undefined;
                }
            } else if (comparisonTimeframe.durationTimeframeType ===
                DurationTimeframeTypeEnum.PreviousSingleTimeFrame) {
                if (typeof comparisonTimeframe.duration !== "undefined") {
                    timeAndDuration.previousRangeType = comparisonTimeframe.duration
                        ? comparisonTimeframe.duration.rangeType
                        : undefined;
                }
            } else if (comparisonTimeframe.durationTimeframeType ===
                DurationTimeframeTypeEnum.SpecificStartOrStop) {
                if (typeof comparisonTimeframe.fixedStartDate !== "undefined") {
                    timeAndDuration.fixedStartDate = comparisonTimeframe.fixedStartDate
                        ? new Date(comparisonTimeframe.fixedStartDate.timestamp)
                        : undefined;
                    timeAndDuration.fixedStartDateRangeType = comparisonTimeframe.fixedStartDate
                        ? comparisonTimeframe.fixedStartDate.rangeType
                        : undefined;
                }
                if (typeof comparisonTimeframe.fixedEndDate !== "undefined") {
                    timeAndDuration.fixedEndDate = comparisonTimeframe.fixedEndDate
                        ? new Date(comparisonTimeframe.fixedEndDate.timestamp)
                        : undefined;
                    timeAndDuration.fixedEndDateRangeType = comparisonTimeframe.fixedEndDate
                        ? comparisonTimeframe.fixedEndDate.rangeType
                        : undefined;
                }
            }

            timeAndDuration.runToCurrentDate = comparisonTimeframe.runToCurrentDate;

            if (typeof comparisonTimeframe.currentDateOffset !== "undefined") {
                timeAndDuration.currentDateOffset = comparisonTimeframe.currentDateOffset
                    ? comparisonTimeframe.currentDateOffset.duration
                    : undefined;
                timeAndDuration.currentDateRangeType = comparisonTimeframe.currentDateOffset
                    ? comparisonTimeframe.currentDateOffset.rangeType
                    : undefined;
            }
        }

        return timeAndDuration;
    }

    public static getConfigurationType(objectType: eLynxObjectTypeEnum): TrendConfigurationTypeEnum {

        let configurationType: TrendConfigurationTypeEnum = TrendConfigurationTypeEnum.Device;

        switch (objectType) {
            case eLynxObjectTypeEnum.Device:
                configurationType = TrendConfigurationTypeEnum.Device;
                break;

            case eLynxObjectTypeEnum.Group:
            case eLynxObjectTypeEnum.TankBattery:

                configurationType = TrendConfigurationTypeEnum.Group;
                break;

            case eLynxObjectTypeEnum.Well:
                configurationType = TrendConfigurationTypeEnum.Well;
                break;
        }

        return configurationType;
    }

    //#region Tank Details configurations
    public static getTankDetailsTrendingConfig(contextObject: IeLynxObject, sectionObjects: Array<IDRCXConfigurationSectionObjectDTO>,
        tankTats: Array<number>, singleSectionName: string) {

        return new TrendConfigurationModel({
            id: DRCXConfigurationModelBase.NEW_CONFIGURATION_ID,
            name: "New Graph",
            configurationType: ConfigurationType.Trending,
            access: AccessType.Private,
            userPermissions: new Array<AccessPermission>(),
            scope: Scope.User,
            created: new Date(),
            forAllGroups: true,
            overrideInstanceDates: true,
            timeframe: {
                durationTimeframeType: DurationTimeframeTypeEnum.MultipleTimeFrames,
                calculatedStartDateOffset: {
                    // Use a negate formatter
                    duration: -30,
                    rangeType: DateRangeType.Day
                },
                duration: {
                    duration: 31,
                    rangeType: DateRangeType.Day
                }
            },
            trendConfigurationType: TrendConfigurationModel.getConfigurationType(contextObject.objectType),
            sections: [
                TrendConfigurationModel._getTankDetailsTrendingDefaultSection(sectionObjects, tankTats, singleSectionName)
            ],

            autoRefresh: false,
            combineAcrossDeviceAxes: false,
            displayAlarms: false,
            displayDataQualityEvents: false,
            displayDiagnosticEvents: false,
            displayManualEvents: false,
            displayLegend: true,
            displayMemos: false,
            displaySectionsStacked: false,
            displayTabularData: false,
            displaySettingsChanges: false,
            displaySimulationDataQualityEvents: false,
            displaySimulationDiagnosticEvents: false,
            hasComparisonTimeframe: false,
            uiChartProperties: new UIChartPropertiesClient()
        });
    }

    private static _getTankDetailsTrendingDefaultSection(sectionObjects: Array<IDRCXConfigurationSectionObjectDTO>, tankTats: Array<number>, singleSectionName: string): TrendConfigurationSectionModel {

        let columnOrder: number = 0;
        //let objectOrder: number = 0;
        let sectionOrder: number = 0;

        //// All Tats
        //let tankTats: Array<number> = Enumerable.from(LiquidProductionTATEnum).select(item =>
        //    parseInt(item.key))
        //    .where(item => item != undefined && !isNaN(item))
        //    .orderBy(item => item)
        //    .toArray();

        //let tankTats: Array<number> = [LiquidProductionTATEnum.TopLevel, LiquidProductionTATEnum.InterfaceLevel];

        const newSection: TrendConfigurationSectionModel = new TrendConfigurationSectionModel({
            name: singleSectionName, //Utilities.StringFormat(i18n.translate("common.configuration.newSection"), 0),
            order: sectionOrder,
            // Trending: always uses Linear for the Style.
            style: Style.Linear,
            dateSortOrder: SortOrder.Ascending,
            deviceSortOrder: SortOrder.Ascending,
            timeframe: {
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: 0,
                    rangeType: DateRangeType.Day
                },
                duration: {
                    duration: 0,
                    rangeType: DateRangeType.Day
                },
                currentDateOffset: {
                    duration: 0,
                    rangeType: DateRangeType.Day
                },
                durationTimeframeType: undefined,
            },
            acrossDevices: false,
            includeOilTanks: false,
            includeWaterTanks: false,
            plotSeriesOnSameYAxis: true,
            // Trending: Seems to always be set to true
            useTatEffectiveDate: true,
            sectionUniqueID: UUID.generate(),
            columns:
                Enumerable.from(tankTats).select(tat => {
                    return new DRCXConfigurationSectionColumnModel({
                        columnType: ColumnType.Tat,
                        //User Story 51646: Stand Alone Tanks - Modify Graph to use Raw Data
                        //groupingFunction: NumericalFunctionEnum.Average,
                        //groupingTimeframe: TimeframeType.Hourly,
                        groupingFunction: NumericalFunctionEnum.None,
                        groupingTimeframe: TimeframeType.Raw,

                        order: columnOrder++,
                        processInDB: true,
                        tagAssociationTypeID: tat
                    });
                }).toArray(),
            objects: sectionObjects
        });

        return newSection;
    }

    // // Alpha Angle, Beta Angle, and Depth.
    // // https://www.highcharts.com/demo/3d-column-interactive
    // public static getTankDetailsTankChartConfig(oilBarrels: number, oilFeet: number, waterBarrels: number, waterFeet: number, tankCapacityBarrels: number, tankHeightFeet: number): HighchartsOptions {

    //     const tickPositionerMargin: number = 12;

    //     const perShapeGradient = {
    //         x1: 0,
    //         y1: 0,
    //         x2: 1,
    //         y2: 0
    //     };

    //     let colors: any = Highcharts.getOptions().colors;

    //     colors = [{
    //         linearGradient: perShapeGradient,
    //         stops: [
    //             [0, 'rgb(128,0,0)'],
    //             [0.1, 'rgb(204,0,0)'],
    //             [0.25, 'rgb(245,128,128)'],
    //             [0.5, 'rgb(230,75,75)'],
    //             [0.75, 'rgb(220,50,50)'],
    //             [0.9, 'rgb(230,75,75)'],
    //             [1, 'rgb(128,0,0)']
    //         ]
    //     }, {
    //         linearGradient: (<any>Highcharts).merge(perShapeGradient),
    //         stops: [
    //             [0, 'rgb(46, 150, 208)'],
    //             [0.5, 'rgb(120, 202, 248)'],
    //             [1, 'rgb(46, 150, 208)']
    //         ]
    //     }, {
    //         linearGradient: (<any>Highcharts).merge(perShapeGradient),
    //         stops: [
    //             [0, 'rgb(112, 180, 5)'],
    //             [0.5, 'rgb(136, 219, 5)'],
    //             [1, 'rgb(112, 180, 5)'],
    //         ]
    //     }, {
    //         linearGradient: (<any>Highcharts).merge(perShapeGradient),
    //         stops: [
    //             [0, 'rgba(192, 192, 192, .5)'],
    //             [0.5, 'rgba(245, 245, 245, .5)'],
    //             [1, 'rgba(192, 192, 192, .5)'],
    //         ]
    //     }];

    //     const leftAxisUnits: string = "bbls";
    //     const rightAxisUnits: string = "Feet";

    //     //const series: Highcharts.SeriesOptions[] = [];
    //     const series: SeriesOptionsType[] = [];

    //     const origOilBarrels: number = oilBarrels;
    //     const origWaterBarrels: number = waterBarrels;

    //     const remainingCapacity: number = Math.abs(tankCapacityBarrels - oilBarrels - waterBarrels);
    //     const overflow: boolean = (oilBarrels + waterBarrels) > tankCapacityBarrels;

    //     if (!overflow) {
    //         series.push({
    //             color: colors[3],
    //             data: [remainingCapacity],
    //             name: i18n.t("common.tankRemainingCapacity"),
    //             yAxis: 0,
    //             showInLegend: true
    //         });
    //     } else {

    //         if (oilBarrels) {
    //             oilBarrels -= remainingCapacity;
    //         } else {
    //             waterBarrels -= remainingCapacity;
    //         }

    //         series.push({
    //             color: colors[0],
    //             data: [remainingCapacity],
    //             name: i18n.translate("common.tankOverflow"),
    //             yAxis: 0,
    //             showInLegend: true
    //         });
    //     }

    //     //const remainingCapacity: number = tankCapacityBarrels - oilBarrels - waterBarrels;

    //     //if (remainingCapacity > 0) {
    //     //    series.push({
    //     //        color: colors[3],
    //     //        data: [remainingCapacity],
    //     //        name: i18n.translate("common.tankRemainingCapacity"),
    //     //        yAxis: 0,
    //     //        showInLegend: true
    //     //    });
    //     //}

    //     if (oilBarrels) {
    //         series.push({
    //             color: colors[2],
    //             data: [oilBarrels],
    //             name: i18n.translate("common.oil"),
    //             yAxis: 0
    //         });
    //     }

    //     if (waterBarrels) {
    //         series.push(
    //             {
    //                 color: colors[1],
    //                 data: [waterBarrels],
    //                 name: i18n.translate("common.water"),
    //                 yAxis: 0
    //             });
    //     }

    //     const bblsMin: number = Math.min(0, oilBarrels, waterBarrels);
    //     const bblsMax: number = Math.max((origOilBarrels + origWaterBarrels), tankCapacityBarrels);

    //     const feetMin: number = Math.min(0, oilFeet, waterFeet);
    //     const feetMax: number = Math.max((oilFeet + waterFeet), tankHeightFeet);

    //     return {
    //         chart: {
    //             ignoreHiddenSeries: false,
    //             marginTop: 50,
    //             type: "cylinder",
    //             options3d: {
    //                 enabled: true,
    //                 alpha: 15,
    //                 beta: 0, //15,
    //                 depth: 50,
    //                 viewDistance: 25
    //             }
    //         },
    //         credits: {
    //             enabled: false
    //         },
    //         exporting: {
    //             enabled: false
    //         },
    //         title: {
    //             text: undefined
    //         },
    //         plotOptions: {
    //             cylinder: {
    //                 pointWidth: 300,
    //                 stacking: 'normal',
    //                 depth: 300,
    //             }
    //         },
    //         series: series,
    //         tooltip: {
    //             headerFormat: undefined
    //         },
    //         xAxis: [{
    //             visible: false
    //         }],
    //         yAxis: [{
    //             //alignTicks: true,
    //             endOnTick: false,
    //             labels: {
    //                 format: "{value} " + leftAxisUnits,
    //             },
    //             min: bblsMin,
    //             max: bblsMax,
    //             plotLines:
    //                 [
    //                     {
    //                         color: "#D3D3D3",
    //                         dashStyle: "solid",
    //                         value: tankCapacityBarrels,
    //                         width: 1,
    //                         zIndex: 999999999
    //                     }],
    //             title: {
    //                 text: undefined
    //             }
    //         },
    //         {
    //             //alignTicks: true,
    //             endOnTick: false,
    //             gridLineWidth: 0,
    //             labels: {
    //                 format: "{value} " + rightAxisUnits,
    //             },
    //             min: feetMin,
    //             max: feetMax,
    //             opposite: true,
    //             showEmpty: true,
    //             title: {
    //                 text: undefined
    //             }
    //         }]
    //     };
    // }

    //#endregion Tank Details configurations

    public toDTO(): ITrendConfigurationDTO {
        return this._toDTO({} as any);
    }

    protected _toDTO(dto: ITrendConfigurationDTO): ITrendConfigurationDTO {

        super._toDTO(dto);

        dto.autoRefresh = this.autoRefresh;
        dto.comparisonTimeframe = this.hasComparisonTimeframe ?
            TrendConfigurationModel.getTimeframeDTO(this.comparisonTimeAndDuration)
            : undefined;
        dto.combineAcrossDeviceAxes = this.combineAcrossDeviceAxes;
        dto.displayAlarms = this.displayAlarms;
        dto.displayDataQualityEvents = this.displayDataQualityEvents;
        dto.displayDiagnosticEvents = this.displayDiagnosticEvents;
        dto.displayManualEvents = this.displayManualEvents;
        dto.displayLegend = this.displayLegend;
        dto.displayMemos = this.displayMemos;
        dto.displaySectionsStacked = this.displaySectionsStacked;
        dto.displayTabularData = this.displayTabularData;
        dto.displaySettingsChanges = this.displaySettingsChanges;
        dto.displaySimulationDataQualityEvents = this.displaySimulationDataQualityEvents;
        dto.displaySimulationDiagnosticEvents = this.displaySimulationDiagnosticEvents;
        dto.hasComparisonTimeframe = this.hasComparisonTimeframe;
        dto.trendConfigurationType = this.trendConfigurationType;
        dto.uiChartProperties = this.uiChartProperties !== undefined ? new UIChartPropertiesClient(this.uiChartProperties) : undefined;

        return dto;
    }

    public static getTimeframeDTO(timeAndDuration: IDRCXTimeAndDuration) {

        let timeframeDTO: IDRCXConfigurationTimeframeDTO = {};

        if (timeAndDuration.durationType === DurationTimeframeTypeEnum.MultipleTimeFrames) {
            timeframeDTO = {
                durationTimeframeType: timeAndDuration.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: timeAndDuration.multipleStartOffset as number,
                    rangeType: timeAndDuration.multipleStartRangeType as DateRangeType
                },
                duration: {
                    duration: timeAndDuration.multipleDuration as number,
                    rangeType: timeAndDuration.multipleDurationRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (timeAndDuration.durationType === DurationTimeframeTypeEnum.CurrentSingleTimeFrame) {
            timeframeDTO = {
                durationTimeframeType: timeAndDuration.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: 0,
                    rangeType: timeAndDuration.currentRangeType as DateRangeType
                },
                duration: {
                    duration: 1,
                    rangeType: timeAndDuration.currentRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (timeAndDuration.durationType === DurationTimeframeTypeEnum.PreviousSingleTimeFrame) {
            timeframeDTO = {
                durationTimeframeType: timeAndDuration.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: -1,
                    rangeType: timeAndDuration.previousRangeType as DateRangeType
                },
                duration: {
                    duration: 1,
                    rangeType: timeAndDuration.previousRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (timeAndDuration.durationType === DurationTimeframeTypeEnum.SpecificStartOrStop) {

            const fixedStartDate = timeAndDuration.fixedStartDate;
            const fixedEndDate = timeAndDuration.fixedEndDate;

            timeframeDTO = {
                durationTimeframeType: timeAndDuration.durationType,
                fixedStartDate: fixedStartDate ? {
                    timestamp: fixedStartDate.toISOString(),
                    rangeType: timeAndDuration.fixedStartDateRangeType as DateRangeType
                } : undefined,
                fixedEndDate: fixedEndDate ? {
                    timestamp: fixedEndDate.toISOString(),
                    rangeType: timeAndDuration.fixedEndDateRangeType as DateRangeType
                } : undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: undefined,
                duration: undefined,
                currentDateOffset: undefined
            };

        } else {
            timeframeDTO = {
                durationTimeframeType: undefined,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: undefined,
                calculatedStartDateOffset: undefined,
                duration: undefined,
                currentDateOffset: undefined
            };
        }

        return timeframeDTO;
    }
}

export interface IGraphDefaultOptions {
    groupName: string;
    objectName?: string;
    isForCurrentUser: boolean;
    isForAllUsers: boolean;
    isForUsersWithoutDefault: boolean;
    isForAllObjectsInCurrentGroupWithoutDefault: boolean;
}

// #region Trending Default

export interface IGraphDefaultDTO {
    allDevicesCurrentGroupWithoutDefault: boolean;
    allUsersDefault: boolean;
    currentUserDefault: boolean;
    trendConfigurationId: number;
    userWithoutDefault: boolean;
}

export class GraphDefaultModel {

    public allDevicesCurrentGroupWithoutDefault: boolean;
    public allUsersDefault: boolean;
    public currentUserDefault: boolean;
    public trendConfigurationId: number;
    public userWithoutDefault: boolean;

    constructor(dto: IGraphDefaultDTO) {

        this.allDevicesCurrentGroupWithoutDefault = dto.allDevicesCurrentGroupWithoutDefault;
        this.allUsersDefault = dto.allUsersDefault;
        this.currentUserDefault = dto.currentUserDefault;
        this.trendConfigurationId = dto.trendConfigurationId;
        this.userWithoutDefault = dto.userWithoutDefault;
    }
}

// #endregion Trending Default


